/**
 * Uniform spatial hash over the playfield.
 *
 * Rebuilt from scratch every sim step. That sounds wasteful but it beats
 * incremental updates here: entities move every frame, counts are in the low
 * thousands, and a full rebuild over flat typed arrays has no allocation and
 * perfect locality. Tower target queries and the flocking solver both use it.
 */

export class SpatialHash {
  readonly cell: number;
  readonly cols: number;
  readonly rows: number;

  /** Start offset into `items` for each cell, length cols*rows + 1. */
  private starts: Int32Array;
  /** Per-cell fill counter, reused across rebuilds. */
  private counts: Int32Array;
  /** Entity indices bucketed by cell. */
  private items: Int32Array;
  private capacity: number;

  constructor(width: number, height: number, cell: number, capacity = 4096) {
    this.cell = cell;
    this.cols = Math.max(1, Math.ceil(width / cell));
    this.rows = Math.max(1, Math.ceil(height / cell));
    const n = this.cols * this.rows;
    this.starts = new Int32Array(n + 1);
    this.counts = new Int32Array(n);
    this.capacity = capacity;
    this.items = new Int32Array(capacity);
  }

  private cellIndex(x: number, y: number): number {
    let cx = (x / this.cell) | 0;
    let cy = (y / this.cell) | 0;
    if (cx < 0) cx = 0; else if (cx >= this.cols) cx = this.cols - 1;
    if (cy < 0) cy = 0; else if (cy >= this.rows) cy = this.rows - 1;
    return cy * this.cols + cx;
  }

  /**
   * Counting sort in two passes. `xs`/`ys` are read at `stride` intervals so
   * callers can pass either flat arrays or interleaved position buffers.
   */
  rebuild(count: number, getX: (i: number) => number, getY: (i: number) => number): void {
    const n = this.cols * this.rows;
    this.counts.fill(0);

    if (count > this.capacity) {
      this.capacity = 1 << Math.ceil(Math.log2(count));
      this.items = new Int32Array(this.capacity);
    }

    for (let i = 0; i < count; i++) this.counts[this.cellIndex(getX(i), getY(i))]++;

    let running = 0;
    for (let c = 0; c < n; c++) {
      this.starts[c] = running;
      running += this.counts[c];
      this.counts[c] = 0;
    }
    this.starts[n] = running;

    for (let i = 0; i < count; i++) {
      const c = this.cellIndex(getX(i), getY(i));
      this.items[this.starts[c] + this.counts[c]++] = i;
    }
  }

  /**
   * Visits every entity index within `radius` of the point. The callback runs
   * for candidates in the covered cells, so it must do its own exact distance
   * test. Returning false stops the walk early.
   */
  query(x: number, y: number, radius: number, visit: (i: number) => boolean | void): void {
    const c = this.cell;
    let x0 = ((x - radius) / c) | 0, x1 = ((x + radius) / c) | 0;
    let y0 = ((y - radius) / c) | 0, y1 = ((y + radius) / c) | 0;
    if (x0 < 0) x0 = 0; if (y0 < 0) y0 = 0;
    if (x1 >= this.cols) x1 = this.cols - 1;
    if (y1 >= this.rows) y1 = this.rows - 1;

    for (let cy = y0; cy <= y1; cy++) {
      const row = cy * this.cols;
      for (let cx = x0; cx <= x1; cx++) {
        const idx = row + cx;
        const s = this.starts[idx], e = this.starts[idx + 1];
        for (let k = s; k < e; k++) {
          if (visit(this.items[k]) === false) return;
        }
      }
    }
  }
}
